import React, { useState, useEffect } from 'react';
import Loading from '../Common/Loading';
import CustomAlert from '../Common/CustomAlert';

// --- Configuration ---
const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

interface WlpRow {
  SchoolCode: string;
  FamilyCode: string;
  Login: string;
  FName: string;
  LName: string;
  StudyYear: string;
  NoOfLessons: number;
  AvlTime: number;
  Remarks: string;
}

const EL_ListWLPsRunningout: React.FC = () => {
  // Query Params
  const queryParams = new URLSearchParams(window.location.search);
  const SchoolCodeParam = queryParams.get('SchoolCode') || '';
  const Subject = queryParams.get('Subject') || '';
  const Source = queryParams.get('Source') || '';
  const FromSupport = true;

  // State
  const [data, setData] = useState<WlpRow[]>([]);
  const [selectedIdx, setSelectedIdx] = useState<number>(-1);
  const [loading, setLoading] = useState(true);
  const [apiLoading, setApiLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });

  // --- Styles ---
  const styles = {
    container: { width: '100%', margin: 0, fontFamily: 'Arial', fontSize: '10pt', backgroundColor: '#fff', minHeight: '100vh' },

    // Grid Container
    gridContainer: {
      width: '100%',
      height: 'calc(100vh - 100px)',
      overflow: 'auto',
      border: '1px solid #80a0ff',
      backgroundColor: 'white'
    },

    // Table
    table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '10pt', border: '1px solid #80a0ff' },

    // Header Styling
    th: {
      backgroundColor: '#c7ddfd',
      color: '#2859ac',
      fontWeight: 'normal',
      padding: '10px',
      border: '1px solid #80a0ff',
      position: 'sticky' as const,
      top: 0,
      textAlign: 'left' as const,
      whiteSpace: 'nowrap' as const
    },
    columnTh: {
      backgroundColor: '#c7ddfd',
      color: '#2859ac',
      fontWeight: 'normal',
      padding: '10px',
      border: '1px solid #80a0ff',
      position: 'sticky' as const,
      top: '40px',
      zIndex: 19,
      textAlign: 'left' as const,
      whiteSpace: 'nowrap' as const
    },

    // Cell Styling
    td: {
      padding: '10px',
      borderRight: '1px solid #80a0ff',
      whiteSpace: 'nowrap' as const,
      color: 'inherit',
      fontSize: '14px'
    },

    // Row Colors
    rowEven: { backgroundColor: '#ffffff', color: '#000' },
    rowOdd: { backgroundColor: '#f4faff', color: '#000' },

    // Selection Styling
    rowSelected: {
      backgroundColor: '#ffa500',
      color: '#fff'
    },

    // Footer & Buttons
    footer: { textAlign: 'center' as const, padding: '10px' },
    btn: {
      cursor: 'pointer',
      padding: '2px 15px',
      margin: '0 10px',
      fontSize: '11pt',
      backgroundColor: 'rgb(239 239 239)',
      color: 'black',
      fontWeight: 'normal',
      border: '1px solid rgb(118 118 118)',
      disabled: { cursor: 'not-allowed', opacity: 0.6 },
      borderRadius: '2px'
    },
    msgBox: {
      color: 'blue',
      fontWeight: 'bold',
      marginTop: '5px'
    }
  };

  // --- Load Data ---
  useEffect(() => {
    fetchList();
  }, []);

  const fetchList = async () => {
    setLoading(true);
    try {
      const url =
        `${API_BASE}api/wlp-running-out-list/` +
        `?SchoolCode=${encodeURIComponent(SchoolCodeParam)}` +
        `&Subject=${encodeURIComponent(Subject)}` +
        `&Source=${encodeURIComponent(Source)}`;
      const res = await fetch(url, { cache: 'no-store' });
      const json = await res.json();

      if (json.error) {
        setAlertInfo({ show: true, message: json.error });
      } else {
        setData(json.data);
        setSelectedIdx(-1);
      }
    } catch (err) {
      setAlertInfo({ show: true, message: 'Failed to load list' });
    } finally {
      setLoading(false);
    }
  };

  // --- Handlers ---
  const validateSelection = () => {
    if (selectedIdx === -1) {
      setAlertInfo({ show: true, message: "Please select a row." });
      return null;
    }
    const row = data[selectedIdx];
    const isBlank = (v: unknown) => String(v ?? '').trim() === '';

    if (isBlank(row.SchoolCode) || isBlank(row.FamilyCode) || isBlank(row.Login)) {
      setAlertInfo({ show: true, message: "One or more among SchoolCode / FamilyCode / Login is empty." });
      return null;
    }
    // Year "0" is valid; only a blank value is rejected (VB: gfn_Trim(...) == '')
    if (isBlank(row.StudyYear)) {
      setAlertInfo({ show: true, message: "Student's Grade/Year is empty.\nCannot extend WLP." });
      return null;
    }
    return row;
  };

  const handleExtend = async (isSelective: boolean) => {
    const row = validateSelection();
    if (!row) return;

    setApiLoading(true);
    try {
      const payload = {
        SchoolCode: row.SchoolCode,
        FamilyCode: row.FamilyCode,
        Login: row.Login,
        StudyYear: row.StudyYear,
        Subject: Subject,
        IsSelective: isSelective
      };

      const res = await fetch(`${API_BASE}api/wlp-extend/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();

      // VB: ErrorMesg popup and/or JobOver popup.
      // A successful regular Extend shows nothing.
      const parts = [json.error, json.message].filter(Boolean);
      if (parts.length) {
        setAlertInfo({ show: true, message: parts.join('\n') });
      }

      // VB re-runs fn_CreateList only when no exception occurred
      if (!json.error) {
        fetchList();
      }
    } catch (err) {
      setAlertInfo({ show: true, message: "Extension Failed" });
    } finally {
      setApiLoading(false);
    }
  };

  const handleCancel = () => {
    if (FromSupport) {
      window.location.href = '/SysAdmin';
    } else {
      window.close();
    }
  };

  const pageTitle =
    Source.toUpperCase() === "MD"
      ? "Maths Doctor WLPs running out. Subject: Mathematics"
      : `Default WLPs running out. Subject: ${Subject}`;

  // --- Render ---
  return (
    <div style={styles.container} id="divMain">
      {apiLoading && <Loading />}
      {alertInfo.show && (
        <CustomAlert
          message={alertInfo.message}
          onClose={() => setAlertInfo({ ...alertInfo, show: false })}
        />
      )}

      <div style={styles.gridContainer}>
        {loading ? (
          <Loading />
        ) : (
          <table style={styles.table}>
            <thead>
              {/* MAIN HEADING ROW */}
              <tr>
                <th
                  colSpan={10}
                  style={{
                    ...styles.th,
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    backgroundColor: 'rgb(199 221 253)',
                    position: 'sticky' as const,
                    top: 0,
                    zIndex: 20,
                    border: '1px solid #80a0ff',
                  }}
                >
                  {pageTitle}
                </th>
              </tr>

              {/* SUB HEADINGS ROW */}
              <tr>
                <th style={{ ...styles.columnTh, textAlign: 'center', width: '40px' }}>#</th>
                <th style={styles.columnTh}>SchoolCode</th>
                <th style={styles.columnTh}>FamilyCode</th>
                {/* <th style={styles.columnTh}>Login</th> */}
                <th style={styles.columnTh}>FName</th>
                <th style={styles.columnTh}>LName</th>
                <th style={{ ...styles.columnTh, textAlign: 'center' }}>StudyYear</th>
                <th style={{ ...styles.columnTh, textAlign: 'center' }}>NoOfLessons</th>
                <th style={{ ...styles.columnTh, textAlign: 'center' }}>AvlTime</th>
                <th style={{ ...styles.columnTh, width: "1000px" }}>Column 1</th>
              </tr>
            </thead>

            <tbody>
              {data.map((row, i) => {
                const isSelected = selectedIdx === i;
                const isOdd = (i + 1) % 2 !== 0;

                let rowStyle = isOdd ? styles.rowOdd : styles.rowEven;
                if (isSelected) rowStyle = styles.rowSelected;

                return (
                  <tr
                    key={i}
                    onClick={() => setSelectedIdx(i)}
                    style={rowStyle}
                  >
                    <td style={{ ...styles.td, textAlign: 'center' }}>{i + 1}</td>
                    <td style={styles.td}>{row.SchoolCode}</td>
                    <td style={styles.td}>{row.FamilyCode}</td>
                    {/* <td style={styles.td}>{row.Login}</td> */}
                    <td style={styles.td}>{row.FName}</td>
                    <td style={styles.td}>{row.LName}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{row.StudyYear}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{row.NoOfLessons}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{row.AvlTime}</td>
                    <td style={styles.td}>{row.Remarks}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div id="divButtons" style={styles.footer}>
        <button
          style={{
            ...styles.btn,
            backgroundColor:
              Source === 'MD' || apiLoading ? '#ccc' : styles.btn.backgroundColor,
            cursor:
              Source === 'MD' || apiLoading ? 'not-allowed' : 'pointer',
            opacity:
              Source === 'MD' || apiLoading ? 0.6 : 1,
          }}
          onClick={() => handleExtend(false)}
          disabled={Source === 'MD' || apiLoading}
        >
          Extend
        </button>

        <button style={styles.btn} onClick={() => window.close()} disabled={apiLoading}>
          Cancel
        </button>

        {/* VB: btnExtendSelectiveLSns is NOT disabled for MD, so only apiLoading blocks it */}
        <button
          style={{
            ...styles.btn,
            backgroundColor: apiLoading ? '#ccc' : styles.btn.backgroundColor,
            cursor: apiLoading ? 'not-allowed' : 'pointer',
            opacity: apiLoading ? 0.6 : 1,
          }}
          onClick={() => handleExtend(true)}
          disabled={apiLoading}
        >
          Extend SelectiveLSns
        </button>

        {/* {FromSupport ? (
          <button style={styles.btn} onClick={handleCancel} disabled={apiLoading}>
            Cancel
          </button>
        ) : (
          <button style={styles.btn} onClick={() => window.close()} disabled={apiLoading}>
            Cancel
          </button>
        )} */}

      </div>
    </div>
  );
};

export default EL_ListWLPsRunningout;